import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { validateOptInProps } from "../../common/utils/validationRules";
import isEqual from 'lodash/isEqual';
import { trackEvent } from "../../utils/analytics";
interface UserState {
  registrationParams: {
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    phoneNumber: string | null;
    region: string | null;
    county: string | null;
    areas: string[];
    password: string | null;
  };
  inputErrors: {
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    phoneNumber: string | null;
    region: string | null;
    county: string | null;
    areas: string[] | string | null;
  };
  user: any;
  loading: boolean;
  canRegister: boolean;
  canPay: boolean;
  changePassSuccess: boolean;
  checkSubmit: boolean;
  feedbackMessage: {
    message: string | null;
    isError: boolean;
    shouldClose: boolean;
    open: boolean;
  };
  optInLoginDialog: {
    open: boolean | false;
    mode: "optIn" | "login";
    subscription: any;
  };
  subscriptions: any;
  subscriptionHistory: [];
  mpesaPayBillNumber: string | null;
  profileDialog: {
    open: boolean | false;
  };
  payment: {
    paymentLoading: boolean;
    paymentSuccess: boolean;
    paymentCheckLoading: boolean;
    paymentCheckSuccess: boolean;
    paymentDone: any;
    attemptFailed: boolean;
  }
  shouldReload: boolean;
}

const initialState: UserState = {
  registrationParams: {
    firstName: null,
    lastName: null,
    email: null,
    phoneNumber: null,
    region: null,
    county: null,
    areas: [],
    password: null,
  },
  user: null,
  inputErrors: {
    firstName: null,
    lastName: null,
    email: null,
    phoneNumber: null,
    region: null,
    county: null,
    areas: [],
  },
  loading: false,
  canRegister: false,
  canPay: false,
  changePassSuccess: false,
  checkSubmit: false,
  feedbackMessage: {
    message: null,
    isError: false,
    open: false,
    shouldClose: false,
  },
  optInLoginDialog: {
    open: false,
    mode: "optIn",
    subscription: null,
  },
  profileDialog: {
    open: false,
  },
  subscriptions: [],
  subscriptionHistory: [],
  mpesaPayBillNumber: null,
  payment: {
    paymentLoading: false,
    attemptFailed: false,
    paymentSuccess: false,
    paymentCheckLoading: false,
    paymentCheckSuccess: false,
    paymentDone: {
      mpesaTrace: '',
      success: false,
    },
  },
  shouldReload: false,
};

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setRegistrationParams: (state, action) => {
      const values = action.payload.values;
      if (values) {
        state.registrationParams = { ...state.registrationParams, ...values };
        const selectedSubscription = state.optInLoginDialog.subscription?.subscription;
        const shouldPay = selectedSubscription && !selectedSubscription.toLowerCase().includes('demand');
        const errors = validateOptInProps(state.registrationParams);
        const mpesaTrace = state.payment.paymentDone.mpesaTrace;
        
        // Clear areas error if areas are selected
        if (state.registrationParams.areas && state.registrationParams.areas.length > 0) {
          errors.areas = null;
        }
        
        // Create a new errors object that properly merges with existing errors
        // but also ensures errors are cleared when they should be
        let newErrors = { ...errors };
        
        // Explicitly ensure areas error is cleared when areas are selected
        if (values.areas && values.areas.length > 0) {
          newErrors.areas = null;
        }
        
        if (!isEqual(state.inputErrors, newErrors)) {
          state.inputErrors = newErrors;
        }
        
        state.canRegister = Object.values(state.inputErrors).every(error => !error) &&
                          (shouldPay ? Boolean(mpesaTrace && mpesaTrace.trim().length > 0) : true);
        state.canPay = Object.values(state.inputErrors).every(error => !error);
      }
    },
    setInputErrors: (state, action) => {
        const errors = action.payload.errors;
        const mpesaTrace = state.payment.paymentDone.mpesaTrace;
        const selectedSubscription = state.optInLoginDialog.subscription?.subscription;
        const shouldPay = selectedSubscription && !selectedSubscription.toLowerCase().includes('demand');
        let newErrors = { ...state.inputErrors, ...errors };
        if (!errors || Object.keys(errors).length === 0) {
            state.inputErrors = initialState.inputErrors;
            newErrors = initialState.inputErrors;
        } else {
            state.inputErrors = newErrors;
        }
      state.canRegister = Object.values(newErrors).every(error => !error) &&
                          (shouldPay ? Boolean(mpesaTrace && mpesaTrace.trim().length > 0) : true);
      state.canPay = Object.values(newErrors).every(error => !error) &&
                      Boolean(mpesaTrace && mpesaTrace.trim().length > 0);
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setOptInLoginDialog: (state, action) => {
      const newSelectedSubscription = action.payload.subscription?.subscription;
      const selectedSubscription = state.optInLoginDialog.subscription?.subscription;
      if (newSelectedSubscription && newSelectedSubscription !== selectedSubscription) {
        const mpesaTrace = state.payment.paymentDone.mpesaTrace;
        const shouldPay = !newSelectedSubscription.toLowerCase().includes('demand');
        state.canRegister = Object.values(state.inputErrors).every(error => !error) &&
                          (shouldPay ? Boolean(mpesaTrace && mpesaTrace.trim().length > 0) : true);
      }
      state.optInLoginDialog = { ...action.payload };
    },
    setProfileDialog: (state, action) => {
      state.profileDialog = { ...action.payload };
    },
    resetDdData: (state, action) => {
        // const {params} = action.payload;
        // if (params.includes('counties')) {
        //     state.registrationParams.county = null;
        // }
        // if (params.includes('areas')) {
        //     state.registrationParams.area = null;
        // }
    },
    setCheckSubmit: (state, action) => {
      state.checkSubmit = action.payload;
    },
    register: (state, action) => {
      if (action.payload.methodRequestState === 'requested') {
        state.loading = true;
      } else if (action.payload.methodRequestState === 'finished') {
        if (action.payload.result) {
          const message = `Welcome to Nijulishe!<br>
          ${!action?.payload?.result?.subscription?.subscription?.toLowerCase().includes('demand') ?
            'Your payment has been processed successfully  and your account has been created.'
              : 'Your account has been created.'}<br>
          You will receive SMS notifications for power interruptions in your area.`;
          state.feedbackMessage = {
            message: message,
            isError: false,
            open: true,
            shouldClose: true,
          };
          state.registrationParams = initialState.registrationParams;
          trackEvent("registration-success", {
            user: state.user?.cnt_id,
            subscription: `${action.payload.result.subscription}`,
          });
        } else {
          trackEvent("registration-failed", {
            user: state.user?.cnt_id,
            error: `Error: ${action.payload.error.message}`,
          });
          state.feedbackMessage = {
            message: action.payload.error.message,
            isError: true,
            open: true,
            shouldClose: false,
          };
        }
        state.loading = false;
      }
    },
    handlePay: (state, action) => {
      if (action.payload.methodRequestState === 'requested') {
        state.payment.paymentLoading = true;
      } else if (action.payload.methodRequestState === 'finished') {
        if (action.payload.result && action.payload.result.payment?.status === '00') {
          trackEvent("payment-success", {
            user: state.user?.cnt_id,
            subscription: `${action.payload.result.subscription}`,
          });
          const message = `Welcome to Nijulishe!<br>
            Your payment has been processsed successfully and your account has been created.<br>
            You will now receive SMS notifications for power interruptions in your area.`;
            state.payment.paymentSuccess = true;
            state.payment.attemptFailed = false;
            state.payment.paymentDone = {
              ...state.payment.paymentDone,
              success: true,
              mpesaTrace: action.payload.result?.payment?.mpesaTrace,
            };
            state.feedbackMessage = {
              message,
              isError: false,
              open: true,
              shouldClose: true,
            };
            state.canRegister = true;
        } else {
          trackEvent("payment-failed", {
            user: state.user?.cnt_id,
            error: action.payload?.error?.message || 'An error occurred while processing your payment.',
          });
          state.payment.paymentSuccess = false;
          state.payment.attemptFailed = true;
          state.payment.paymentDone = {
            ...state.payment.paymentDone,
            success: false,
            mpesaTrace: null,
          };
          state.feedbackMessage = {
            message: action.payload?.error?.message || 'An error occurred while processing your payment.',
            isError: true,
            open: true,
            shouldClose: false,
          };

        }
        state.payment.paymentLoading = false;
      }
    },
    resetOptInFeedbackMessage: (state, action) => {
      state.feedbackMessage = {
        message: null,
        isError: false,
        open: false,
        shouldClose: false,
      };
      // state.payment.paymentSuccess = false;
      // state.payment.paymentDone = {
      //   ...state.payment.paymentDone,
      //   success: false,
      //   mpesaTrace: null,
      // };
      // state.payment.paymentLoading = false;
    },
    resetProfileFeedbackMessage: (state, action) => {
      state.feedbackMessage = {
        message: null,
        isError: false,
        open: false,
        shouldClose: false,
      };
    },
    fetchSubscriptions: (state, action) => {
      if (action.payload.methodRequestState === 'finished' && action.payload.result) {
        state.subscriptions = action.payload.result.subscriptions;
        state.mpesaPayBillNumber = action.payload.result.mpesaPayBillNumber;
      }
    },
    userOptOut: (state, action) => {
      if (action.payload.methodRequestState === 'requested') {
        state.loading = true;
      } else if (action.payload.methodRequestState === 'finished' && action.payload.result) {
        state.feedbackMessage = {
          message: 'You have been opted out successfully',
          isError: false,
          open: true,
          shouldClose: true,
        };
        state.loading = false;
      } else {
        state.loading = false;
        state.feedbackMessage = {
          message: action.payload?.error?.message || 'An error occurred while opting out',
          isError: true,
          open: true,
          shouldClose: false,
        };
      }
    },
    updateProfile: (state, action) => {
      if (action.payload.methodRequestState === 'finished' && action.payload.result) {
        trackEvent("profile-update-success", JSON.stringify({
          user: state.user?.cnt_id,
        }));
        // state.loggedInUser = action.payload.result;
        state.feedbackMessage = {
          message: 'Profile updated successfully',
          isError: false,
          open: true,
          shouldClose: true,
        };
        state.user = action.payload.result;
        state.shouldReload = true;
      }
    },
    getUserData: (state, action) => {
      if (action.payload.methodRequestState === 'requested') {
        state.loading = true;
      } else if (action.payload.methodRequestState === 'finished' && action.payload.result) {
        state.user = action.payload.result;
        state.loading = false;
      } else {
        state.loading = false;
        state.feedbackMessage = {
          message: action.payload.error.message,
          isError: true,
          open: true,
          shouldClose: false,
        };
      }
    },
    setOptInFeedbackMessage: (state, action) => {
      state.feedbackMessage = {
        message: action.payload.message,
        isError: action.payload.isError,
        open: true,
        shouldClose: false,
      };
    },
    upgradeSubscription: (state, action) => {
      if (action.payload.methodRequestState === 'requested') {
        state.loading = true;
      } else if (action.payload.methodRequestState === 'finished') {
        if (action.payload.result) {
          trackEvent("subscription-upgrade-success", JSON.stringify({
            user: state.user?.cnt_id,
            subscription: action.payload.result.subscription,
          }));
          state.feedbackMessage = {
            message: 'Subscription upgraded successfully!',
            isError: false,
            open: true,
            shouldClose: true,
          };
          state.shouldReload = true;
          state.user = action.payload.result?.user || state.user;
          // If onSuccess callback is provided, call it
          if (action.payload.onSuccess) {
            action.payload.onSuccess(action.payload.result);
          }
        } else {
          trackEvent("subscription-upgrade-failed", JSON.stringify({
            user: state.user?.cnt_id,
            error: action.payload.error?.message || 'Failed to upgrade subscription.',
          }));
          state.feedbackMessage = {
            message: action.payload.error?.message || 'Failed to upgrade subscription.',
            isError: true,
            open: true,
            shouldClose: false,
          };
        }
        state.loading = false;
      }
    },
    fetchSubscriptionHistory: (state, action) => {
      if (action.payload.methodRequestState === 'requested') {
        state.loading = true;
      } else if (action.payload.methodRequestState === 'finished' && action.payload.result) {
        state.subscriptionHistory = action.payload.result;
        state.loading = false;
      } else {
        state.loading = false;
        state.feedbackMessage = {
          message: action.payload.error.message,
          isError: true,
          open: true,
          shouldClose: false,
        };
      }
    },
    changePassword: (state, action) => {
      // This is just a placeholder, the actual logic is in extraReducers
      if (action.payload.methodRequestState === 'requested') {
        state.loading = true;
        state.changePassSuccess = false;
      } else if (action.payload.methodRequestState === 'finished') {
        if (action.payload.result?.success) {
          trackEvent("password-change-success", JSON.stringify({
            user: state.user?.cnt_id,
          }));
          state.feedbackMessage = {
            message: 'Password changed successfully',
            isError: false,
            open: true,
            shouldClose: false,
          };
          state.changePassSuccess = true;
          state.user = action.payload.result?.user || state.user;
          state.shouldReload = true;
          if (action.payload.onSuccess) {
            action.payload.onSuccess();
          }
        } else {
          trackEvent("password-change-failed", JSON.stringify({
            user: state.user?.cnt_id,
            error: action.payload.error?.message || 'Failed to change password',
          }));
          state.feedbackMessage = {
            message: action.payload.error?.message || 'Failed to change password',
            isError: true,
            open: true,
            shouldClose: false,
          };
          if (action.payload.onError) {
            action.payload.onError(action.payload.error);
          }
        }
        state.changePassSuccess = false;
        state.loading = false;
      }
    },
    resetShouldReload: (state) => {
      state.shouldReload = false;
    },
  },
});

export const {
  setRegistrationParams,
  setInputErrors,
  setLoading,
  register,
  resetDdData,
  setCheckSubmit,
  resetOptInFeedbackMessage,
  setOptInLoginDialog,
  fetchSubscriptions,
  updateProfile,
  resetProfileFeedbackMessage,
  getUserData,
  setProfileDialog,
  handlePay,
  setOptInFeedbackMessage,
  upgradeSubscription,
  fetchSubscriptionHistory,
  changePassword,
  resetShouldReload,
  userOptOut,
} = userSlice.actions;

export const registerAction = ({ params, method }: { params: any; method: { url: string; action: string } }) => ({
    type: register.type,
    payload: {
      params,
      method,
    }
  });

export const fetchSubscriptionsAction = ({ params ={}, method }: { params: any; method: { url: string; action: string } }) => ({
    type: fetchSubscriptions.type,
    payload: {
      params,
      method,
    }
  });

export const updateProfileAction = ({ params, method }: { params: any; method: { url: string; action: string } }) => {
  // Map form fields to expected API fields if needed
  const apiParams: {
    cntId: any;
    areaIds: any[];
    firstName: any;
    phoneNumber: any;
    email?: any;
    lastName?: any;
  } = {
    cntId: params.cnt_id,
    areaIds: params.area_ids || [],
    firstName: params.first_name,
    phoneNumber: params.msisdn,
  };

  if (params.email) {
    apiParams.email = params.email;
  }

  if (params.last_name) {
    apiParams.lastName = params.last_name;
  }

  return {
    type: updateProfile.type,
    payload: {
      params: apiParams,
      method,
    }
  };
};

export const getUserDataAction = ({ params }: { params: any }) => ({
  type: getUserData.type,
  payload: {
    params,
    method: {
      url: '/users/userData',
      action: 'POST',
    }
  }
});

export const handlePayAction = ({ params, method }: { params: any; method: { url: string; action: string } }) => ({
  type: handlePay.type,
  payload: {
    params,
    method,
  }
});

export const userOptOutAction = ({ params, method }: { params: any; method: { url: string; action: string } }) => ({
  type: userOptOut.type,
  payload: {
    params,
    method,
  }
});

export const selectUserState = (state: RootState) => state.user;

export default userSlice.reducer;

export const upgradeSubscriptionAction = (payload: any) => ({
  type: upgradeSubscription.type,
  payload: {
    params: payload.params,
    method: {
      url: '/payments/changePlan',
      action: 'POST',
    },
  },
});

export const fetchSubscriptionHistoryAction = (payload: any) => ({
  type: fetchSubscriptionHistory.type,
  payload: {
    params: payload.params,
    method: {
      url: '/users/subscriptions/getHistory',
      action: 'POST',
    },
  },
});

export const changePasswordAction = ({ params, method, onSuccess, onError }: { 
  params: any; 
  method: { url: string; action: string };
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
}) => ({
  type: changePassword.type,
  payload: {
    params,
    method,
    onSuccess,
    onError
  },
});
